import { ExtendedStateManager } from 'react-extended-state'
import type { IntlShape } from '@formatjs/intl'

import { EventListener } from '../../utils'
import {
    type AppState,
    Capability,
    ConnectwareError,
    ConnectwareErrorType,
    ResourceType,
    selectAuthenticatedCapabilitiesWithDefault,
    selectAuthenticatedToken,
    selectAuthenticatedUsername,
    selectMfaRequiredAuthenticationToken,
} from '../../domain'
import type {
    ApplicationStateService,
    AuthenticationPersistenceService,
    AuthenticationService,
    BrowserService,
    CertificatesService,
    ClientRegistryService,
    ConfigurationService,
    ConnectwareLogsService,
    ConnectwareResourcesManagementService,
    ConnectwareServicesCatalogService,
    ConnectwareServicesService,
    FileService,
    LoggerService,
    MfaService,
    RuleEngineService,
    SubscriptionsService,
    SystemService,
    TablePersistenceService,
    TopicsPersistenceService,
    TopicsService,
    TranslationService,
    UsecaseServices,
    UserService,
} from '../../application'

import { HTML5BrowserService } from '../HTML5'
import { createTranslationInternationalization, FormatJSTranslationService } from '../FormatJS'
import { WebStorageAuthenticationPersistenceService, WebStorageTablePersistenceService, WebStorageTopicsPersistenceService } from '../webstorage'
import { WebFileService } from '../FileService'
import { CybusConfigurationService } from '../CybusConfiguration'
import {
    ConnectwareHTTPAuthenticationService,
    ConnectwareHTTPCertificatesService,
    ConnectwareHTTPClientRegistryService,
    ConnectwareHTTPLogsService,
    ConnectwareHTTPMfaService,
    ConnectwareHTTPResourcesManagementService,
    ConnectwareHTTPRuleEngineService,
    type ConnectwareHTTPServiceOptions,
    ConnectwareHTTPServicesCatalogService,
    ConnectwareHTTPServicesService,
    ConnectwareHTTPSubscriptionsService,
    ConnectwareHTTPTopicsService,
    ConnectwareHTTPUserService,
    CybusSystemService,
    type HttpResponse,
    isMissingPermissionsResponse,
} from '../http'
import { type MqttMessageFromWorker, type MqttMessageToWorker, MqttTopicsService } from '../mqtt'
import { VrpcConnectwareResourcesManagementService, VrpcDomainType, VrpcRemoteManager, VrpcSubscriptionsService } from '../vrpc'
import type { TranslationProviderProps } from '../react/Internationalization'
import { WrappedWorker } from '../workers'
import type { ConnectwareAdminWebAppConfig } from '../config'

import { MixedConnectwareResourcesManagementService, MixedSubscriptionsService } from './Mixin'
import { getMqttWorkerRef } from './workers'

/**
 * This class is here to enable the PermissionSplitter to do its job
 * But it also collects the many tecnologies used on the Admin UI
 * to be injected into the application's usecases
 */
class InjectedServices implements UsecaseServices {
    readonly subscriptionsService: SubscriptionsService

    readonly connectwareResourcesManagementService: ConnectwareResourcesManagementService

    readonly connectwareLogsService: ConnectwareLogsService

    readonly connectwareServicesCatalogService: ConnectwareServicesCatalogService

    readonly connectwareServicesService: ConnectwareServicesService

    readonly authenticationService: AuthenticationService

    readonly browserService: BrowserService

    readonly authenticationPersistenceService: AuthenticationPersistenceService

    readonly userService: UserService

    readonly clientRegistryService: ClientRegistryService

    readonly configurationService: ConfigurationService

    readonly systemService: SystemService

    readonly fileService: FileService

    readonly translationService: TranslationService

    readonly topicsService: TopicsService

    readonly ruleEngineService: RuleEngineService

    readonly topicsPersistenceService: TopicsPersistenceService

    readonly certificatesService: CertificatesService

    readonly tablePersistenceService: TablePersistenceService

    readonly mfaService: MfaService

    constructor (
        readonly applicationState: ApplicationStateService,
        readonly logger: LoggerService,
        intl: IntlShape<string>,
        config: ConnectwareAdminWebAppConfig,
        httpResponseListener: EventListener<HttpResponse>
    ) {
        const {
            version,
            salesEmail,
            supportEmail,
            servicesCatalogDefaultImageLink,
            protectedRoles,
            protectedUsers,
            userManagementPagination,
            userManagementIndexCache,
            usernamePolicy,
            backendHost,
            servicesExternalUrlBasePath,
            workbenchPath,
            vrpc,
            mqtt,
        } = config

        /**
         * Either use the backend custom host, or fallback to the current url
         */
        const host = backendHost ?? location.host
        const httpsBackendURL = new URL(`https://${host}`)
        const wssBackendURL = new URL(`wss://${host}`)

        const createStateSelector =
            <T, A extends unknown[]>(selector: (s: AppState, ...args: A) => T): ((...args: A) => T) =>
            (...args) =>
                selector(this.applicationState.getState(), ...args)

        const selectCapabilities = createStateSelector(selectAuthenticatedCapabilitiesWithDefault)
        const getToken = createStateSelector(selectAuthenticatedToken)
        const tokenOrMfaTokenSelector = createStateSelector((s) => selectMfaRequiredAuthenticationToken(s) ?? selectAuthenticatedToken(s))
        const baseMqttAuthenticationSelector = createStateSelector((state, path: string) => {
            const username = selectAuthenticatedUsername(state)
            const password = selectAuthenticatedToken(state)

            if (password === null || username === null) {
                throw new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'Authentication is missing')
            }

            return { url: new URL(path, wssBackendURL), username, password }
        })

        this.translationService = new FormatJSTranslationService(intl)

        /**
         * Web services
         */
        this.browserService = new HTML5BrowserService(
            logger,
            httpsBackendURL.origin,
            new Set([httpsBackendURL.host, location.host]),
            new Set([servicesExternalUrlBasePath, workbenchPath])
        )
        this.fileService = new WebFileService()
        this.authenticationPersistenceService = new WebStorageAuthenticationPersistenceService(window.localStorage, version)
        this.topicsPersistenceService = new WebStorageTopicsPersistenceService(window.localStorage, version)
        this.tablePersistenceService = new WebStorageTablePersistenceService(window.localStorage, version)

        /**
         * Due to how changes are expected on the domain and business layers, propagating resource changes SHOULD ONLY
         * be done under the hood and without extra work from the rendering layer
         *
         * This listener hooks "the http resource service" to "the http subscription service" and
         * ensures that whenever a service changes, there will be an immediate change on the UI
         */
        const resourceChangeListeners = new EventListener<void>()

        /**
         * Http services
         */
        const httpOptions: ConnectwareHTTPServiceOptions = { baseURL: origin, getToken, responseHandler: httpResponseListener }
        this.ruleEngineService = new ConnectwareHTTPRuleEngineService(httpOptions)
        this.userService = new ConnectwareHTTPUserService(
            httpOptions,
            {
                usersConfig: { protectedRoles: new Set(protectedRoles), protectedUsers: new Set(protectedUsers) },
                pagination: userManagementPagination,
                cacheInterval: userManagementIndexCache,
                usernamePolicy,
            },
            this.translationService
        )
        this.clientRegistryService = new ConnectwareHTTPClientRegistryService(httpOptions, this.translationService)
        this.authenticationService = new ConnectwareHTTPAuthenticationService(httpOptions)
        this.configurationService = new CybusConfigurationService(
            { httpsURL: httpsBackendURL, wssURL: wssBackendURL, salesEmail, supportEmail, mqttPaths: [vrpc.path, mqtt.path], workbenchPath, version },
            new ConnectwareHTTPTopicsService(httpOptions, this.translationService)
        )
        this.systemService = new CybusSystemService(httpOptions, version)
        this.certificatesService = new ConnectwareHTTPCertificatesService(httpOptions)

        const httpSubscriptionsService = new ConnectwareHTTPSubscriptionsService({
            ...httpOptions,
            getCapabilities: selectCapabilities,
            translationService: this.translationService,
            resourceChangeListeners,
        })

        const httpConnectwareResourcesService = new ConnectwareHTTPResourcesManagementService(httpOptions, this.translationService, resourceChangeListeners)
        this.connectwareLogsService = new ConnectwareHTTPLogsService(httpOptions, this.translationService)
        this.connectwareServicesCatalogService = new ConnectwareHTTPServicesCatalogService(
            httpOptions,
            this.configurationService,
            this.logger,
            servicesCatalogDefaultImageLink
        )
        this.connectwareServicesService = new ConnectwareHTTPServicesService(
            httpOptions,
            this.configurationService,
            this.translationService,
            resourceChangeListeners
        )
        this.mfaService = new ConnectwareHTTPMfaService({ ...httpOptions, getToken: tokenOrMfaTokenSelector }, this.translationService)

        /**
         * Mqtt services
         */
        this.topicsService = new MqttTopicsService(() => ({
            ...baseMqttAuthenticationSelector(mqtt.path),
            cycle: mqtt.cycle,
            maxTopicDepth: mqtt.maxTopicDepth,
            timeout: mqtt.timeout,
            worker: new WrappedWorker<MqttMessageToWorker, MqttMessageFromWorker>(getMqttWorkerRef()),
        }))

        /**
         * Vrpc services
         *
         * Wait a full second before disconnection
         */
        const remote = new VrpcRemoteManager({
            remoteArgs: () => {
                const { url, ...args } = baseMqttAuthenticationSelector(vrpc.path)
                return { ...args, timeout: vrpc.timeout, broker: url.toString(), bestEffort: true }
            },
            agentLoadGracePeriod: 500,
            disconnectGracePeriod: 5_000,
            logger,
            domains: { [VrpcDomainType.DEFAULT]: 'internal.cybus', [VrpcDomainType.EDGE]: 'edge.cybus' },
        })

        const vrpcConnectwareResourcesService = new VrpcConnectwareResourcesManagementService(remote, this.translationService)
        const vrpcSubscriptionsService = new VrpcSubscriptionsService(remote, this.translationService)

        this.subscriptionsService = new MixedSubscriptionsService(
            {},
            selectCapabilities,
            { [Capability.LEGACY_ADMIN_PERMISSION]: vrpcSubscriptionsService },
            httpSubscriptionsService
        )

        this.connectwareResourcesManagementService = new MixedConnectwareResourcesManagementService(
            { [ResourceType.CORE_CONTAINER]: httpConnectwareResourcesService, [ResourceType.SERVICE]: httpConnectwareResourcesService },
            selectCapabilities,
            { [Capability.LEGACY_ADMIN_PERMISSION]: vrpcConnectwareResourcesService },
            httpConnectwareResourcesService
        )
    }
}

type ParcelInjections = Readonly<{
    services: UsecaseServices

    state: ExtendedStateManager<AppState>
    internationalization: TranslationProviderProps['internationalization']

    missingPermissionsListener: Pick<EventListener<HttpResponse>, 'on'>
}>

export const resolveInjections = (appState: AppState, config: ConnectwareAdminWebAppConfig): ParcelInjections => {
    const state = new ExtendedStateManager<AppState>(appState)
    const logger: LoggerService = console
    const internationalization = createTranslationInternationalization(config.translations, logger)
    const missingPermissionsListener = new EventListener<HttpResponse>(isMissingPermissionsResponse)

    return {
        services: new InjectedServices(state, logger, internationalization, config, missingPermissionsListener),
        state,
        internationalization,
        missingPermissionsListener,
    }
}
